'use client';

import { Check, File, Phone, Plus, Search, UsersRound, X } from 'lucide-react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, {
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
} from 'react';
import { useDebounce } from 'use-debounce';
import DeferredMount from '@/components/ui/DeferredMount';
import { MultiFormatImage } from '@/components/ui/MultiFormatImage';
import { useInquiry } from '@/contexts/InquiryContext';
import { useProductContext } from '@/contexts/ProductContext';
import { trackCTAClick, trackEvent, trackTimeSpent } from '@/lib/analytics';

// Number of cards in the first grid row (matches the xl:grid-cols-4 breakpoint,
// the widest column count used). These are the images most likely to be the
// LCP candidate, so they get `priority` instead of being deprioritized like
// the rest of the (below-the-fold) grid.
const DESKTOP_PRIORITY_CARD_COUNT = 4;
const MOBILE_PRIORITY_CARD_COUNT = 1;

// SSR-safe media query hook — returns the matches result, defaulting to
// false (mobile-first) during SSR so the server HTML matches the mobile view.
function useMediaQuery(query) {
    return useSyncExternalStore(
        (callback) => {
            const mql = window.matchMedia(query);
            mql.addEventListener('change', callback);
            return () => mql.removeEventListener('change', callback);
        },
        () => window.matchMedia(query).matches,
        () => false, // SSR fallback
    );
}

// Lazy-load below-the-fold sections to reduce initial bundle size
const WhyChooseSection = dynamic(
    () => import('@/components/products/WhyChooseSection'),
    { ssr: false },
);
const EquipmentApplicationsSection = dynamic(
    () => import('@/components/products/EquipmentApplicationsSection'),
    { ssr: false },
);
const CTA = dynamic(() => import('@/components/ui/CTA'), {
    ssr: false,
});

import Chip from '@/components/ui/Chip';

// Mapping configurations for clean categorization
const SUITABLE_MAPPINGS = [
    {
        keyword: 'trench shield',
        text: 'Utility work, sewer installation, water mains, general excavation',
    },
    {
        keyword: 'octo',
        text: 'Large manholes, formwork protection, service installation',
    },
    {
        keyword: 'versa shield',
        text: 'Modular trenching, custom configurations, variable widths',
    },
    {
        keyword: 'versa manhole',
        text: 'Manholes, laterals, pump access, lift wells',
    },
    {
        keyword: 'trench box',
        text: 'Box formation trenching, foundation work, enclosed spaces',
    },
    {
        keyword: 'litebox',
        text: 'Smaller trenches, lightweight applications, quick setup',
    },
    {
        keyword: 'siltmate',
        text: 'Sediment control, water quality protection, construction runoff',
    },
    {
        keyword: 'supa shield',
        text: 'Extreme projects, deep excavations, high load conditions',
    },
];

const CATEGORY_MAPPINGS = [
    { keyword: 'shield', exclude: 'box', category: 'trench-shields' },
    { keyword: 'manhole', category: 'manhole-shields' },
    { keyword: 'versa', category: 'shoring-systems' },
    { keyword: 'octo', category: 'manhole-shields' },
    { keyword: 'modular', category: 'shoring-systems' },
    { keyword: 'siltmate', category: 'specialty-shields' },
    { keyword: 'specialist', category: 'specialty-shields' },
    { keyword: 'supa', category: 'specialty-shields' },
];

// Helper function to find spec values
const findSpecValue = (specs, keywords) => {
    if (!specs || !Array.isArray(specs)) return 'Contact for specs';
    const spec = specs.find(
        (spec) =>
            spec.label &&
            keywords.some((keyword) =>
                spec.label.toLowerCase().includes(keyword),
            ),
    );
    return spec?.value || 'Contact for specs';
};

// Helper function to determine suitable applications
const getSuitableText = (productName) => {
    const mapping = SUITABLE_MAPPINGS.find((m) =>
        productName.toLowerCase().includes(m.keyword),
    );
    return mapping?.text || 'Professional excavation work';
};

// Helper function to determine category
const _getProductCategory = (productName) => {
    const mapping = CATEGORY_MAPPINGS.find(
        (m) =>
            productName.toLowerCase().includes(m.keyword) &&
            (!m.exclude || !productName.toLowerCase().includes(m.exclude)),
    );
    return mapping?.category || 'trench-boxes';
};

// Loading skeleton component that reflects the real card layout (image, title, metadata, CTAs)
const EquipmentCardSkeleton = () => (
    <article
        className="bg-white shadow-sm border border-gray-200 overflow-hidden"
        aria-hidden="true"
    >
        {/* Image area */}
        <div className="relative aspect-4/3 bg-linear-to-r from-gray-200 via-gray-100 to-gray-200">
            <div className="absolute inset-0 motion-safe:animate-pulse motion-reduce:opacity-100" />
        </div>

        {/* Content */}
        <div className="p-6">
            {/* Title */}
            <div className="h-6 bg-gray-200 rounded mb-3 motion-safe:animate-pulse motion-reduce:opacity-100 w-3/4" />

            {/* Short description lines */}
            <div className="space-y-2 mb-4">
                <div className="h-4 bg-gray-200 rounded motion-safe:animate-pulse motion-reduce:opacity-100 w-full" />
                <div className="h-4 bg-gray-200 rounded motion-safe:animate-pulse motion-reduce:opacity-100 w-5/6" />
            </div>

            {/* Features / specs placeholder */}
            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="h-3 bg-gray-100 rounded motion-safe:animate-pulse motion-reduce:opacity-100" />
                <div className="h-3 bg-gray-100 rounded motion-safe:animate-pulse motion-reduce:opacity-100" />
                <div className="h-3 bg-gray-100 rounded motion-safe:animate-pulse motion-reduce:opacity-100" />
                <div className="h-3 bg-gray-100 rounded motion-safe:animate-pulse motion-reduce:opacity-100" />
            </div>

            {/* CTA buttons */}
            <div className="flex gap-3">
                <div className="h-10 bg-gray-200 rounded flex-1 motion-safe:animate-pulse motion-reduce:opacity-100" />
                <div className="h-10 bg-gray-200 rounded w-20 motion-safe:animate-pulse motion-reduce:opacity-100" />
            </div>
        </div>
    </article>
);

// Component for grid view equipment card
const EquipmentCard = React.memo(({ item, priority = false }) => {
    const primaryLink = item.href || '/contact';
    const [isAdding, setIsAdding] = useState(false);
    const headingId = `product-title-${item.id}`;

    const { addToInquiry, isInInquiry } = useInquiry();

    const handleAddToInquiry = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsAdding(true);

        const inquiryItem = {
            type: 'product',
            name: item.name,
            image: item.img,
            description: item.description,
            specifications: {
                Size: item.size,
                Weight: item.weight,
                Suitable: item.suitable,
            },
        };

        addToInquiry(inquiryItem);

        // Reset the adding state after a short delay
        setTimeout(() => {
            setIsAdding(false);
        }, 1000);
    };

    const isInList = isInInquiry({
        type: 'product',
        name: item.name,
    });

    return (
        <li className="bg-white shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-200 hover:border-brand/50 group list-none flex flex-col h-full">
            {/* Image */}
            <Link
                href={primaryLink}
                onClick={() =>
                    trackEvent('product_click', {
                        product: item.name,
                        id: item.id,
                        category: item.category,
                    })
                }
                aria-labelledby={headingId}
                className="focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
                <div className="aspect-4/3 bg-linear-to-br from-gray-50 to-gray-100 relative overflow-hidden cursor-pointer">
                    <div className="p-8 w-full h-full flex items-center justify-center">
                        <MultiFormatImage
                            src={item.img}
                            alt={`${item.name} - Professional trench protection equipment`}
                            width={400}
                            height={300}
                            className="w-full h-full object-contain"
                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 33vw, 25vw"
                            priority={priority}
                        />
                    </div>
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-linear-to-t from-brand/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
            </Link>

            {/* Content */}
            <div className="p-6 flex flex-col flex-1">
                <h2
                    id={headingId}
                    className="font-bold text-xl text-gray-900 mb-3 leading-tight group-hover:text-brand/80 transition-colors duration-200"
                >
                    {item.name}
                </h2>
                <p className="text-gray-600 text-sm mb-6 line-clamp-2 leading-relaxed">
                    {item.description}
                </p>

                {/* Call to action */}
                <div className="flex flex-col items-center flex-wrap gap-3 mt-auto">
                    <button
                        type="button"
                        onClick={handleAddToInquiry}
                        disabled={isAdding}
                        className={`appearance-none w-full flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-none transition-all duration-200 font-semibold text-sm hover:cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 active:scale-[0.98] uppercase tracking-wide border ${
                            isInList
                                ? 'bg-green-600 hover:bg-green-700 text-white border-green-700'
                                : isAdding
                                  ? 'bg-brand-light text-white opacity-80 border-brand-light'
                                  : 'bg-brand-dark hover:bg-[#A83800] text-white border-brand-dark'
                        }`}
                        aria-label={
                            isInList
                                ? `${item.name} added to inquiry`
                                : `Add ${item.name} to inquiry`
                        }
                    >
                        {isAdding ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Adding...
                            </>
                        ) : isInList ? (
                            <>
                                <Check className="w-4 h-4" />
                                Added to Inquiry
                            </>
                        ) : (
                            <>
                                <Plus className="w-4 h-4" />
                                Add to Inquiry
                            </>
                        )}
                    </button>
                    {item.href && (
                        <Link
                            href={item.href}
                            onClick={() =>
                                trackEvent('cta_view_details', {
                                    product: item.name,
                                    id: item.id,
                                    category: item.category,
                                })
                            }
                            aria-labelledby={headingId}
                            className="appearance-none w-full flex-1 inline-flex items-center justify-center gap-2 bg-white hover:bg-brand-dark text-brand-dark hover:text-white hover:cursor-pointer px-4 py-3 rounded-none transition-all duration-200 font-semibold text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-dark focus-visible:ring-offset-2 active:scale-[0.98] uppercase tracking-wide border border-brand-dark"
                            aria-label={`View details for ${item.name}`}
                        >
                            <File className="w-4 h-4" />
                            View Details
                        </Link>
                    )}
                </div>
            </div>
        </li>
    );
});

EquipmentCard.displayName = 'EquipmentCard';

export default function ClientProducts({
    serverProducts,
    bgSrcSet,
    bgBlurDataURL,
}) {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [announceText, setAnnounceText] = useState('');
    const [_showMoreCategories, setShowMoreCategories] = useState(false);

    // Only mark the first card as priority on mobile to reduce bandwidth
    // contention — on desktop, mark the first row (4 cards) for LCP.
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const priorityCardCount = isDesktop
        ? DESKTOP_PRIORITY_CARD_COUNT
        : MOBILE_PRIORITY_CARD_COUNT;

    const { products: contextProducts } = useProductContext();

    // Use server-provided products for the initial SSR render so the product
    // grid is present in the first HTML payload. This avoids the skeleton→content
    // transition and dramatically improves Speed Index (Lighthouse penalises
    // late-appearing content). If the context eventually provides different data
    // (e.g. after a client-side revalidation), prefer the fresher data.
    const products =
        contextProducts.length > 0 ? contextProducts : (serverProducts ?? []);

    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const lastCategoryRef = React.useRef(selectedCategory);

    // Track time spent on products page
    React.useEffect(() => {
        const pageStartTime = Date.now();

        return () => {
            const timeSpent = (Date.now() - pageStartTime) / 1000;
            if (timeSpent > 1) {
                trackTimeSpent('products_page', timeSpent);
            }
        };
    }, []);

    // Debounce search input for better performance
    const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

    // Memoize equipment data transformation for performance
    const equipment = useMemo(() => {
        if (!products.length) return [];

        return products.map((product) => {
            // Extract size information using helper function
            const lengthValue = findSpecValue(product.specs, [
                'length',
                'size',
            ]);
            const heightValue = findSpecValue(product.specs, ['height']);
            const widthValue = findSpecValue(product.specs, [
                'width',
                'internal width',
            ]);

            let size = 'Contact for specs';
            if (
                lengthValue !== 'Contact for specs' &&
                heightValue !== 'Contact for specs'
            ) {
                size = `${lengthValue} x ${heightValue}`;
            } else if (lengthValue !== 'Contact for specs') {
                size = lengthValue;
            } else if (widthValue !== 'Contact for specs') {
                size = widthValue;
            }

            // Extract weight from variants
            let weight = 'Contact for specs';
            if (product.variants && product.variants.length > 0) {
                const firstVariant = product.variants[0];
                if (firstVariant.specs) {
                    weight = findSpecValue(firstVariant.specs, ['weight']);
                }
            }

            const stableId =
                product.id || product.slug || product.href || product.name;

            return {
                id: stableId,
                name: product.name,
                description: product.description,
                img: product.img,
                category: getProductCategories(product.name), // <-- now an array
                size,
                weight,
                suitable: getSuitableText(product.name),
                features: product.features || [
                    'Professional grade',
                    'Safety certified',
                    'NZ manufactured',
                ],
                href: product.href,
                background: product.background,
            };
        });
    }, [products]);

    // Memoize categories calculation
    const categories = useMemo(
        () => [
            {
                id: 'all',
                name: 'All Equipment',
                count: equipment.length,
            },
            {
                id: 'trench-boxes',
                name: 'Boxes',
                count: equipment.filter(
                    (e) =>
                        e.category.includes('trench-boxes') &&
                        !e.name.toLowerCase().includes('accessory') &&
                        !e.name.toLowerCase().includes('accessories'),
                ).length,
            },
            {
                id: 'trench-shields',
                name: 'Shields',
                count: equipment.filter(
                    (e) =>
                        e.category.includes('trench-shields') &&
                        !e.name.toLowerCase().includes('accessory') &&
                        !e.name.toLowerCase().includes('accessories'),
                ).length,
            },
            {
                id: 'manhole-shields',
                name: 'Manhole Shields',
                count: equipment.filter(
                    (e) =>
                        e.category.includes('manhole-shields') &&
                        !e.name.toLowerCase().includes('accessory') &&
                        !e.name.toLowerCase().includes('accessories'),
                ).length,
            },
            {
                id: 'shoring-systems',
                name: 'Shoring Systems',
                count: equipment.filter(
                    (e) =>
                        e.category.includes('shoring-systems') &&
                        !e.name.toLowerCase().includes('accessory') &&
                        !e.name.toLowerCase().includes('accessories'),
                ).length,
            },
            {
                id: 'specialty-shields',
                name: 'Specialty Shields',
                count: equipment.filter(
                    (e) =>
                        e.category.includes('specialty-shields') &&
                        !e.name.toLowerCase().includes('accessory') &&
                        !e.name.toLowerCase().includes('accessories'),
                ).length,
            },
        ],
        [equipment],
    );

    // Memoize filtered equipment with debounced search
    const filteredEquipment = useMemo(() => {
        return equipment.filter((item) => {
            const isAccessory =
                item.name.toLowerCase().includes('accessory') ||
                item.name.toLowerCase().includes('accessories');
            const matchesCategory =
                selectedCategory === 'all'
                    ? true
                    : item.category.includes(selectedCategory) && !isAccessory;
            const matchesSearch =
                item.name
                    .toLowerCase()
                    .includes(debouncedSearchTerm.toLowerCase()) ||
                item.description
                    .toLowerCase()
                    .includes(debouncedSearchTerm.toLowerCase()) ||
                item.suitable
                    .toLowerCase()
                    .includes(debouncedSearchTerm.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    }, [equipment, selectedCategory, debouncedSearchTerm]);

    // Announce filtered result counts to screen readers when search/category changes
    useEffect(() => {
        const count = filteredEquipment.length;
        let text = '';

        if (debouncedSearchTerm) {
            const term = debouncedSearchTerm.trim();
            text = `${count} result${count === 1 ? '' : 's'} for "${term}"`;
        } else if (selectedCategory && selectedCategory !== 'all') {
            // Humanize category id if possible
            const label =
                categories.find((c) => c.id === selectedCategory)?.name ||
                selectedCategory;
            text = `${count} result${count === 1 ? '' : 's'} in ${label}`;
        } else {
            text = `${count} result${count === 1 ? '' : 's'}`;
        }

        // Small delay to ensure screen readers notice changes when rapid updates occur
        const t = setTimeout(() => setAnnounceText(text), 100);
        return () => clearTimeout(t);
    }, [
        filteredEquipment.length,
        debouncedSearchTerm,
        selectedCategory,
        categories,
    ]);

    // Initialize from URL query params so deep links reproduce filter state
    useEffect(() => {
        try {
            const cat = searchParams.get('category');
            const q = searchParams.get('q');

            if (cat) {
                setSelectedCategory(cat);
                // Ensure our lastCategoryRef matches the URL-initialized category
                // so the sync effect doesn't treat this as a user-initiated category change
                lastCategoryRef.current = cat;
            } else {
                // explicitly set to default
                lastCategoryRef.current = 'all';
            }

            if (q !== null && q !== undefined) setSearchTerm(q);
        } catch (_e) {
            // ignore if searchParams not ready
        }
        // only run when the URL params object changes
    }, [searchParams]);

    // Track search analytics (debounced)
    useEffect(() => {
        const q = debouncedSearchTerm?.trim() || '';
        if (q !== '') {
            trackEvent('search', {
                query: q,
                results: filteredEquipment.length,
            });
        } else {
            trackEvent('search_clear');
        }
    }, [debouncedSearchTerm, filteredEquipment.length]);

    // Category selection handler with analytics
    const handleSelectCategory = (id) => {
        setSelectedCategory(id);
        trackEvent('select_category', { category: id });
    };

    // Sync selectedCategory + debouncedSearchTerm to the URL (shallow-like replace)
    useEffect(() => {
        if (!pathname) return;

        // Build new query string
        const params = new URLSearchParams(window.location.search);

        if (selectedCategory && selectedCategory !== 'all') {
            params.set('category', selectedCategory);
        } else {
            params.delete('category');
        }

        if (debouncedSearchTerm && debouncedSearchTerm.trim() !== '') {
            params.set('q', debouncedSearchTerm.trim());
        } else {
            params.delete('q');
        }

        const newSearch = params.toString();
        const currentSearch = searchParams.toString();
        const currentUrl = currentSearch
            ? `${pathname}?${currentSearch}`
            : pathname;
        const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname;

        // Avoid no-op navigations that can retrigger render cycles in production.
        if (newUrl === currentUrl) {
            return;
        }

        // Use push when the category changed (user intent). Use replace for search changes to avoid noisy history.
        const prevCategory = lastCategoryRef.current;
        if (prevCategory !== selectedCategory) {
            // category changed -> push a new history entry
            router.push(newUrl);
            lastCategoryRef.current = selectedCategory;
        } else {
            // search changed only -> replace current history entry
            router.replace(newUrl);
        }
    }, [selectedCategory, debouncedSearchTerm, pathname, router, searchParams]);

    const clearFilters = () => {
        setSelectedCategory('all');
        setSearchTerm('');
        setShowMoreCategories(false);
        // Analytics
        trackEvent('clear_filters');
        // Clear query params and create history entry
        router.push(window.location.pathname);
    };

    const humanize = (name) =>
        name
            .replace(' Shields', '')
            .replace(' Systems', '')
            .replace('Trench ', '');

    // Loading state
    if (!products.length) {
        return (
            <div className="min-h-screen bg-gray-100">
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="text-center mb-12">
                        <div className="motion-safe:animate-pulse motion-reduce:animate-none">
                            <div
                                className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"
                                aria-hidden="true"
                            ></div>
                            <div
                                className="h-4 bg-gray-200 rounded w-96 mx-auto"
                                aria-hidden="true"
                            ></div>
                        </div>
                    </div>
                    <ul
                        className="grid grid-cols-1 md:grid-cols-3 2xl:grid-cols-4 gap-8 list-none"
                        aria-label="Equipment loading"
                        aria-busy="true"
                    >
                        {[
                            'skeleton-1',
                            'skeleton-2',
                            'skeleton-3',
                            'skeleton-4',
                            'skeleton-5',
                            'skeleton-6',
                        ].map((id) => (
                            <EquipmentCardSkeleton key={id} />
                        ))}
                    </ul>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Screen reader live region for announcing result counts */}
            <output aria-live="polite" className="sr-only">
                {announceText}
            </output>
            {/* PageHeader is rendered server-side in src/app/products/page.js */}

            {/* Equipment Display */}
            <section
                id="equipment"
                className="bg-white scroll-mt-20 scroll-smooth"
            >
                <div className="bg-white border-b border-gray-200 shadow-sm">
                    <div className="w-full max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
                        {/* Single Row Layout */}
                        <div className="mx-auto ">
                            {/* Modern Search Field */}
                            <div className="relative flex-1 w-full">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search equipment..."
                                    value={searchTerm}
                                    onChange={(e) =>
                                        setSearchTerm(e.target.value)
                                    }
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand focus:border-brand transition-all duration-200 bg-white text-gray-900 placeholder-gray-600"
                                    aria-label="Search equipment"
                                />

                                {/* Filter Chips Inside Search - DESKTOP ONLY */}
                                {!searchTerm && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 hidden md:flex items-center gap-1">
                                        <Chip
                                            onClick={() =>
                                                handleSelectCategory('all')
                                            }
                                            active={selectedCategory === 'all'}
                                        >
                                            All
                                        </Chip>
                                        {categories
                                            .filter(
                                                (c) =>
                                                    c.id !== 'all' &&
                                                    c.count > 0,
                                            )
                                            .slice(0, 3)
                                            .map((category) => (
                                                <Chip
                                                    key={category.id}
                                                    onClick={() =>
                                                        handleSelectCategory(
                                                            category.id,
                                                        )
                                                    }
                                                    active={
                                                        selectedCategory ===
                                                        category.id
                                                    }
                                                >
                                                    {humanize(category.name)}
                                                </Chip>
                                            ))}

                                        {/* Clear filters control */}
                                        <button
                                            type="button"
                                            onClick={clearFilters}
                                            aria-label="Clear filters"
                                            className="ml-2 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 cursor-pointer transition-colors"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                )}

                                {/* Clear button and result count when searching */}
                                {searchTerm && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                                        <span className="text-xs text-gray-400">
                                            {filteredEquipment.length} found
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchTerm('');
                                                trackEvent(
                                                    'search_clear_button',
                                                );
                                            }}
                                            className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                                            aria-label="Clear search"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Filter Chips Below Search - MOBILE ONLY */}
                            {!searchTerm && (
                                <div className="flex md:hidden items-center gap-1 mt-3 flex-wrap">
                                    <Chip
                                        onClick={() =>
                                            handleSelectCategory('all')
                                        }
                                        size="md"
                                        active={selectedCategory === 'all'}
                                    >
                                        All
                                    </Chip>
                                    {/** show up to 4 mobile chips */}
                                    {categories
                                        .filter(
                                            (c) =>
                                                c.id !== 'all' && c.count > 0,
                                        )
                                        .slice(0, 4)
                                        .map((category) => (
                                            <Chip
                                                key={category.id}
                                                onClick={() =>
                                                    handleSelectCategory(
                                                        category.id,
                                                    )
                                                }
                                                size="md"
                                                active={
                                                    selectedCategory ===
                                                    category.id
                                                }
                                            >
                                                {humanize(category.name)}
                                            </Chip>
                                        ))}

                                    <button
                                        type="button"
                                        onClick={clearFilters}
                                        aria-label="Clear filters"
                                        className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 cursor-pointer transition-colors"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="mx-auto">
                        {/* Only render grid view */}
                        <ul
                            className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-8 list-none"
                            aria-label="Equipment catalog list"
                        >
                            {filteredEquipment.map((item, index) => (
                                <EquipmentCard
                                    key={item.id}
                                    item={item}
                                    priority={index < priorityCardCount}
                                />
                            ))}
                        </ul>

                        {/* No Results */}
                        {filteredEquipment.length === 0 && (
                            <div className="text-center py-16 bg-gray-100 border border-gray-200">
                                <Search className="w-16 h-16 text-gray-400 mx-auto mb-6" />
                                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                    No equipment found
                                </h2>
                                <p className="text-gray-600 text-lg mb-6 max-w-md mx-auto">
                                    Try adjusting your search or give us a call
                                    - we might have what you need!
                                </p>
                                <Link
                                    href="/contact"
                                    onClick={() =>
                                        trackCTAClick('Contact Us', {
                                            cta_section: 'products_no_results',
                                            cta_type: 'primary',
                                        })
                                    }
                                    className="inline-flex items-center bg-brand hover:bg-brand-dark text-white px-6 py-3 rounded-lg transition-all duration-200 font-semibold cursor-pointer active:scale-[0.98]"
                                >
                                    <Phone className="w-5 h-5 mr-2" />
                                    Contact Us
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Why Choose + Equipment Applications — lazy-loaded AND deferred
                until they scroll near the viewport, so their JS doesn't
                compete with the critical rendering path's main-thread time
                right after hydration (reduces TBT). */}
            <DeferredMount minHeight="240px">
                <WhyChooseSection />
            </DeferredMount>
            <DeferredMount minHeight="240px">
                <EquipmentApplicationsSection />
            </DeferredMount>
            <DeferredMount minHeight="200px">
                <CTA
                    heading="Ready to Get Started?"
                    subtext="Contact our team today to discuss your project requirements and discover how Trenchmate can help you achieve safer, more efficient construction outcomes."
                    primaryButton={{
                        href: '/contact',
                        label: 'Contact Us',
                        icon: <Phone className="w-5 h-5 mr-2" />,
                        trackingLabel: 'Contact Us',
                    }}
                    secondaryButton={{
                        href: '/about',
                        label: 'About Us',
                        icon: <UsersRound className="w-5 h-5 mr-2" />,
                        trackingLabel: 'Learn More',
                    }}
                    ctaSection="products_bottom_cta"
                />
            </DeferredMount>
        </>
    );
}

const getProductCategories = (productName) => {
    const name = productName.toLowerCase();
    const categories = [];

    // Octobox logic: must be in both boxes and manhole
    if (name.includes('octo')) {
        categories.push('trench-boxes', 'manhole-shields');
    }

    // Versa Manhole Boxes logic: must be in both boxes and manhole
    if (name.includes('versa manhole')) {
        categories.push('trench-boxes', 'manhole-shields');
    }

    // Specialist Solutions logic: add to trench-shields
    if (name.includes('specialist solutions')) {
        categories.push('trench-shields');
    }

    CATEGORY_MAPPINGS.forEach((m) => {
        if (
            name.includes(m.keyword) &&
            (!m.exclude || !name.includes(m.exclude))
        ) {
            if (!categories.includes(m.category)) {
                categories.push(m.category);
            }
        }
    });
    // Do not assign GN Solids Control products to 'trench-boxes'
    const isGN =
        name.includes('gn solids') || name.includes('gn solids control');

    // Remove trench-boxes for GN products if it was added by other rules
    if (isGN) {
        const idx = categories.indexOf('trench-boxes');
        if (idx !== -1) categories.splice(idx, 1);
    }

    // Default to 'trench-boxes' only when no categories matched and it's not a GN product
    if (categories.length === 0 && !isGN) categories.push('trench-boxes');
    return categories;
};
